import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SidebarComponent } from '../../components/sidebar/sidebar.component';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { BrandGroupService } from '../../core/services/brand-group.service';
import { BrandService } from '../../core/services/brand.service';
import { DeviceModelService } from '../../core/services/device-model.service';
import { BrandGroup } from '../../models/brand-group.interface';
import { BrandItem } from '../../models/brand.interface';
import { DeviceModelItem } from '../../models/device-model.interface';

@Component({
  selector: 'app-brand-groups',
  standalone: true,
  imports: [CommonModule, FormsModule, SidebarComponent, NavbarComponent],
  template: `
    <app-sidebar></app-sidebar>
    <app-navbar
      pageTitle="Brand Groups"
      pageSubtitle="Group device models under brand series (e.g. Apple Group 1 = iPhone 13/14/15 Pro Max)"
    ></app-navbar>

    <div class="page-wrapper">
      <main class="main-content animate-in">
        <!-- HEADER BAR -->
      <div class="page-header glass-card">
        <div class="page-header-left">
          <div class="icon-box icon-box-gold icon-box-md">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div>
            <h2 class="font-serif title-gold" style="font-size:16px;">Brand Groups Manager</h2>
            <p class="sub-text">{{ brandGroups.length }} groups configured</p>
          </div>
        </div>
        <button class="btn-gold" id="add-group-btn" (click)="openAddForm()">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          New Group
        </button>
      </div>

      <!-- GROUP SEARCH & FILTER BAR -->
      <div class="brand-tabs-bar" style="display: flex; gap: 12px; align-items: center; flex-wrap: wrap;">
        <div style="flex: 1; min-width: 240px;">
          <input
            type="text"
            class="form-control"
            placeholder="Search group by name or models (e.g. GLASS CASE, iPhone 16)..."
            [(ngModel)]="searchQuery"
            (ngModelChange)="applyFilter()"
          />
        </div>
        <button
          class="brand-tab"
          [class.active]="selectedGroupFilter === 'all'"
          (click)="filterByGroup('all')"
        >All Groups <span class="tab-count">{{ brandGroups.length }}</span></button>
        <button
          *ngFor="let g of brandGroups"
          class="brand-tab"
          [class.active]="selectedGroupFilter === g.name"
          (click)="filterByGroup(g.name)"
        >
          {{ g.name }}
          <span class="tab-count">{{ g.deviceModels.length }}</span>
        </button>
      </div>

      <!-- GROUPS GRID -->
      <div class="groups-grid" *ngIf="filteredGroups.length > 0">
        <div *ngFor="let group of filteredGroups" class="group-card glass-card" id="group-{{ group._id }}">
          <div class="group-card-header">
            <div class="group-info">
              <h3 class="group-name" style="font-size: 16px; color: var(--accent-gold);">{{ group.name }}</h3>
              <span class="group-label" *ngIf="group.displayLabel" style="font-size: 12px; color: var(--text-secondary); margin-top: 2px;">
                {{ group.displayLabel }}
              </span>
            </div>
            <div class="group-actions">
              <button class="action-btn action-btn-gold" title="Edit" (click)="editGroup(group)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
              <button class="action-btn action-btn-danger" title="Delete" (click)="confirmDelete(group)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                </svg>
              </button>
            </div>
          </div>

          <div class="model-chips">
            <span *ngFor="let model of group.deviceModels" class="model-chip">{{ model }}</span>
            <span *ngIf="group.deviceModels.length === 0" class="sub-text" style="font-size:11px;">No device models added yet</span>
          </div>

          <div class="group-footer">
            <span class="sub-text">{{ group.deviceModels.length }} device model{{ group.deviceModels.length !== 1 ? 's' : '' }}</span>
            <span class="status-chip" [class.active]="group.active">{{ group.active ? 'Active' : 'Inactive' }}</span>
          </div>
        </div>
      </div>

      <!-- EMPTY STATE -->
      <div *ngIf="filteredGroups.length === 0" class="empty-state glass-card">
        <div class="empty-icon">📱</div>
        <h3 class="font-serif title-gold">No Brand Groups</h3>
        <p class="sub-text">Create your first brand group to link device models together</p>
        <button class="btn-gold" (click)="openAddForm()">Create First Group</button>
      </div>

    </main>

    <!-- ADD / EDIT MODAL -->
    <div class="modal-overlay" *ngIf="showForm" (click)="closeForm()">
      <div class="modal-panel glass-card" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <h3 class="font-serif title-gold">{{ editMode ? 'Edit Brand Group' : 'Create Brand Group' }}</h3>
          <button class="close-btn" (click)="closeForm()">×</button>
        </div>

        <form (ngSubmit)="onSave()" class="modal-form">
          <div class="form-group">
            <label class="form-label">Group Name *</label>
            <input
              type="text"
              class="form-control"
              [(ngModel)]="formData.name"
              name="name"
              placeholder="e.g. GLASS CASE, SOFT CASE, or PRO SERIES"
              required
              style="font-size: 14px; font-weight: 600;"
            />
          </div>

          <div class="form-group">
            <label class="form-label">Display Label (Optional)</label>
            <input
              type="text"
              class="form-control"
              [(ngModel)]="formData.displayLabel"
              name="displayLabel"
              placeholder="e.g. 9H Tempered Glass Back Case"
            />
          </div>

          <div class="form-group">
            <label class="form-label">Description</label>
            <input
              type="text"
              class="form-control"
              [(ngModel)]="formData.description"
              name="description"
              placeholder="Optional description"
            />
          </div>

          <!-- Device Models Section -->
          <div class="form-group">
            <label class="form-label">Device Models in this Group</label>
            <div class="model-input-row">
              <input
                type="text"
                class="form-control"
                [(ngModel)]="newModelInput"
                name="newModelInput"
                placeholder="e.g. iPhone 15 Pro Max"
                (keydown.enter)="addModel($event)"
              />
              <button type="button" class="btn-gold btn-sm" (click)="addModel($event)">Add</button>
            </div>

            <!-- Existing device models from API (checkboxes) -->
            <div class="device-checkboxes" *ngIf="deviceModels.length > 0">
              <p class="sub-text" style="font-size:11px; margin-bottom: 8px;">Quick select from existing models:</p>
              <div class="checkboxes-grid">
                <label *ngFor="let dev of deviceModels" class="checkbox-label">
                  <input
                    type="checkbox"
                    [checked]="isModelSelected(dev.name)"
                    (change)="toggleModel(dev.name)"
                  />
                  <span>{{ dev.name }}</span>
                </label>
              </div>
            </div>

            <!-- Selected models preview -->
            <div class="selected-models" *ngIf="formData.deviceModels && formData.deviceModels.length > 0">
              <p class="sub-text" style="font-size:11px; margin-bottom: 6px;">Selected ({{ formData.deviceModels.length }}):</p>
              <div class="model-chips">
                <span *ngFor="let m of formData.deviceModels; let i = index" class="model-chip model-chip-rm">
                  {{ m }}
                  <button type="button" (click)="removeModel(i)">×</button>
                </span>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" [(ngModel)]="formData.active" name="active" />
              <span>Active (visible in product form)</span>
            </label>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn-secondary" (click)="closeForm()">Cancel</button>
            <button type="submit" class="btn-gold" [disabled]="saving">
              {{ saving ? 'Saving...' : (editMode ? 'Update Group' : 'Create Group') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- DELETE CONFIRM MODAL -->
    <div class="modal-overlay" *ngIf="deleteTarget" (click)="deleteTarget = null">
      <div class="modal-panel modal-sm glass-card" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <h3 style="color: var(--danger); font-size: 15px;">Delete Brand Group</h3>
          <button class="close-btn" (click)="deleteTarget = null">×</button>
        </div>
        <p class="sub-text" style="margin-bottom: 20px;">
          Are you sure you want to delete <strong style="color: var(--text-primary);">{{ deleteTarget.name }}</strong>?
          This action cannot be undone.
        </p>
        <div class="modal-footer">
          <button class="btn-secondary" (click)="deleteTarget = null">Cancel</button>
          <button class="btn-danger" [disabled]="deleting" (click)="doDelete()">
            {{ deleting ? 'Deleting...' : 'Yes, Delete' }}
          </button>
        </div>
      </div>
    </div>
  </div>
  `,
  styles: [`
    .page-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 20px 24px;
      margin-bottom: 20px;
    }

    .page-header-left {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    /* Brand Filter Tabs */
    .brand-tabs-bar {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      margin-bottom: 24px;
    }

    .brand-tab {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 16px;
      background: transparent;
      border: 1px solid var(--border-color);
      border-radius: var(--radius-full);
      color: var(--text-secondary);
      font-size: 12.5px;
      font-weight: 600;
      cursor: pointer;
      transition: all var(--transition-fast);
      font-family: var(--font-sans);
    }

    .brand-tab:hover {
      border-color: var(--accent-gold);
      color: var(--accent-gold);
    }

    .brand-tab.active {
      background: var(--accent-gold-glow);
      border-color: var(--accent-gold);
      color: var(--accent-gold);
    }

    .tab-count {
      background: rgba(255,255,255,0.08);
      padding: 1px 6px;
      border-radius: var(--radius-full);
      font-size: 10px;
    }

    /* Groups Grid */
    .groups-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
      gap: 20px;
    }

    .group-card {
      padding: 20px;
      transition: transform var(--transition-fast), box-shadow var(--transition-fast);
    }

    .group-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 32px rgba(0,0,0,0.3);
    }

    .group-card-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      margin-bottom: 16px;
    }

    .group-info { flex: 1; }

    .brand-chip {
      display: inline-block;
      padding: 2px 10px;
      background: var(--accent-gold-glow);
      border: 1px solid rgba(212, 175, 55, 0.3);
      border-radius: var(--radius-full);
      color: var(--accent-gold);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      margin-bottom: 6px;
    }

    .group-name {
      font-size: 15px;
      font-weight: 700;
      color: var(--text-primary);
      margin: 0 0 4px;
    }

    .group-label {
      font-size: 11px;
      color: var(--text-muted);
    }

    .group-actions {
      display: flex;
      gap: 6px;
    }

    .action-btn {
      width: 30px;
      height: 30px;
      border-radius: var(--radius-sm);
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all var(--transition-fast);
    }

    .action-btn-gold {
      background: var(--accent-gold-glow);
      color: var(--accent-gold);
    }

    .action-btn-gold:hover {
      background: var(--accent-gold);
      color: #0B0F17;
    }

    .action-btn-danger {
      background: var(--danger-bg);
      color: var(--danger);
    }

    .action-btn-danger:hover {
      background: var(--danger);
      color: #fff;
    }

    /* Model Chips */
    .model-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 14px;
    }

    .model-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 3px 10px;
      background: rgba(255,255,255,0.05);
      border: 1px solid var(--border-color);
      border-radius: var(--radius-full);
      font-size: 11px;
      color: var(--text-secondary);
    }

    .model-chip-rm button {
      background: none;
      border: none;
      color: var(--danger);
      cursor: pointer;
      font-size: 13px;
      line-height: 1;
      padding: 0;
    }

    .group-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-top: 12px;
      border-top: 1px solid var(--border-subtle);
    }

    .status-chip {
      padding: 2px 10px;
      border-radius: var(--radius-full);
      font-size: 10px;
      font-weight: 700;
      background: var(--danger-bg);
      color: var(--danger);
    }

    .status-chip.active {
      background: rgba(74, 222, 128, 0.08);
      color: #4ADE80;
    }

    /* Empty State */
    .empty-state {
      padding: 60px 32px;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
    }

    .empty-icon { font-size: 48px; }

    /* Modal */
    .modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.7);
      backdrop-filter: blur(4px);
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
    }

    .modal-panel {
      width: 100%;
      max-width: 640px;
      max-height: 90vh;
      overflow-y: auto;
      padding: 28px;
    }

    .modal-sm {
      max-width: 420px;
    }

    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 24px;
      padding-bottom: 16px;
      border-bottom: 1px solid var(--border-color);
    }

    .close-btn {
      background: transparent;
      border: none;
      color: var(--text-muted);
      font-size: 22px;
      cursor: pointer;
      line-height: 1;
      padding: 0;
    }

    .close-btn:hover { color: var(--text-primary); }

    .modal-form { display: flex; flex-direction: column; gap: 16px; }

    .form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }

    .model-input-row {
      display: flex;
      gap: 10px;
      margin-bottom: 12px;
    }

    .model-input-row .form-control { flex: 1; }

    .btn-sm {
      padding: 8px 16px;
      font-size: 12px;
      white-space: nowrap;
    }

    .device-checkboxes {
      padding: 12px;
      background: rgba(0,0,0,0.2);
      border: 1px solid var(--border-color);
      border-radius: var(--radius-md);
      margin-bottom: 12px;
    }

    .checkboxes-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 6px;
      max-height: 180px;
      overflow-y: auto;
    }

    .selected-models {
      padding: 12px;
      background: rgba(0,0,0,0.15);
      border: 1px solid var(--border-color);
      border-radius: var(--radius-md);
    }

    .modal-footer {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      padding-top: 20px;
      border-top: 1px solid var(--border-color);
    }

    .btn-danger {
      background: var(--danger);
      color: #fff;
      border: none;
      padding: 10px 20px;
      border-radius: var(--radius-md);
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      font-family: var(--font-sans);
      transition: all var(--transition-fast);
    }

    .btn-danger:hover { opacity: 0.85; }
    .btn-danger:disabled { opacity: 0.5; cursor: not-allowed; }
  `]
})
export class BrandGroupsComponent implements OnInit {
  brandGroups: BrandGroup[] = [];
  filteredGroups: BrandGroup[] = [];
  brands: BrandItem[] = [];
  deviceModels: DeviceModelItem[] = [];

  selectedBrandFilter = 'all';
  selectedGroupFilter = 'all';
  searchQuery = '';
  showForm = false;
  editMode = false;
  saving = false;
  deleteTarget: BrandGroup | null = null;
  deleting = false;
  newModelInput = '';

  formData: Partial<BrandGroup> = {
    name: '',
    brandSlug: '',
    displayLabel: '',
    deviceModels: [],
    description: '',
    active: true
  };

  private editingId: string | null = null;

  constructor(
    private brandGroupService: BrandGroupService,
    private brandService: BrandService,
    private deviceModelService: DeviceModelService
  ) {}

  ngOnInit(): void {
    this.loadBrandGroups();
    this.loadBrands();
    this.loadDeviceModels();
  }

  loadBrandGroups(): void {
    this.brandGroupService.getBrandGroups().subscribe({
      next: (groups) => {
        this.brandGroups = groups;
        this.applyFilter();
      },
      error: (err) => console.error('Failed to load brand groups:', err)
    });
  }

  loadBrands(): void {
    this.brandService.getBrands().subscribe({
      next: (brands) => (this.brands = brands),
      error: (err) => console.error('Failed to load brands:', err)
    });
  }

  loadDeviceModels(): void {
    this.deviceModelService.getDeviceModels().subscribe({
      next: (models) => (this.deviceModels = models),
      error: (err) => console.error('Failed to load device models:', err)
    });
  }

  filterByBrand(slug: string): void {
    this.selectedBrandFilter = slug;
    this.applyFilter();
  }

  filterByGroup(groupName: string): void {
    this.selectedGroupFilter = groupName;
    this.applyFilter();
  }

  applyFilter(): void {
    let result = this.brandGroups;

    if (this.selectedGroupFilter !== 'all') {
      result = result.filter(g => g.name === this.selectedGroupFilter);
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      result = result.filter(g =>
        g.name.toLowerCase().includes(q) ||
        (g.displayLabel || '').toLowerCase().includes(q) ||
        (g.deviceModels || []).some(m => m.toLowerCase().includes(q))
      );
    }

    this.filteredGroups = result;
  }

  countGroupsForBrand(slug: string): number {
    return this.brandGroups.filter(g => g.brandSlug === slug).length;
  }

  getBrandName(slug: string): string {
    const found = this.brands.find(b => b.slug === slug);
    return found ? found.name : slug;
  }

  openAddForm(): void {
    this.editMode = false;
    this.editingId = null;
    this.formData = { name: '', brandSlug: '', displayLabel: '', deviceModels: [], description: '', active: true };
    this.newModelInput = '';
    this.showForm = true;
  }

  editGroup(group: BrandGroup): void {
    this.editMode = true;
    this.editingId = group._id || null;
    this.formData = {
      name: group.name,
      brandSlug: group.brandSlug,
      displayLabel: group.displayLabel || '',
      deviceModels: [...group.deviceModels],
      description: group.description || '',
      active: group.active
    };
    this.newModelInput = '';
    this.showForm = true;
  }

  closeForm(): void {
    this.showForm = false;
  }

  isModelSelected(name: string): boolean {
    return (this.formData.deviceModels || []).includes(name);
  }

  toggleModel(name: string): void {
    if (!this.formData.deviceModels) this.formData.deviceModels = [];
    const idx = this.formData.deviceModels.indexOf(name);
    if (idx > -1) {
      this.formData.deviceModels.splice(idx, 1);
    } else {
      this.formData.deviceModels.push(name);
    }
  }

  addModel(event: Event): void {
    event.preventDefault();
    const m = this.newModelInput.trim();
    if (!m) return;
    if (!this.formData.deviceModels) this.formData.deviceModels = [];
    if (!this.formData.deviceModels.includes(m)) {
      this.formData.deviceModels.push(m);
    }
    this.newModelInput = '';
  }

  removeModel(index: number): void {
    this.formData.deviceModels?.splice(index, 1);
  }

  onSave(): void {
    if (!this.formData.name) {
      alert('Group Name is required.');
      return;
    }

    this.saving = true;

    if (this.editMode && this.editingId) {
      this.brandGroupService.updateBrandGroup(this.editingId, this.formData).subscribe({
        next: () => {
          this.saving = false;
          this.showForm = false;
          this.loadBrandGroups();
        },
        error: (err) => {
          this.saving = false;
          alert('Failed to update: ' + err.message);
        }
      });
    } else {
      this.brandGroupService.createBrandGroup(this.formData).subscribe({
        next: () => {
          this.saving = false;
          this.showForm = false;
          this.loadBrandGroups();
        },
        error: (err) => {
          this.saving = false;
          alert('Failed to create: ' + err.message);
        }
      });
    }
  }

  confirmDelete(group: BrandGroup): void {
    this.deleteTarget = group;
  }

  doDelete(): void {
    if (!this.deleteTarget?._id) return;
    this.deleting = true;
    this.brandGroupService.deleteBrandGroup(this.deleteTarget._id).subscribe({
      next: () => {
        this.deleting = false;
        this.deleteTarget = null;
        this.loadBrandGroups();
      },
      error: (err) => {
        this.deleting = false;
        alert('Failed to delete: ' + err.message);
      }
    });
  }
}
